import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import usePipelinesApi, {
  CardLossReasonItem,
  EditCardPayload,
  GetCardByIdResponse,
  LabelItem,
  SaveCardPayload,
  StageItem,
  UpdateCardFormValues
} from "../Pipeline/use-pipelines";
import {
  ContactAutocompleteWrapper,
  ContactSuggestionItem,
  ContactSuggestions,
  Container,
  Content,
  LabelChip,
  LabelMultiSelectWrapper,
  NameIcon,
  OptionsButtons,
  StageDropdown
} from "./styles";
import { Button, useToast } from "../..";
import { AuthContextType, useAuth } from "../../../auth/auth-provider";
import { v } from "../../../util";
import Avatar from "../../../omnichannel/avatar";
import { MultiSelect } from "primereact/multiselect";
import PhoneMask from "../../../components/phone-mask";
import Input from "../../../components/input-text";
import { Contacts } from "../../../assets";
import useContacts from "../../ContactsList/use-contacts";
import WorkspaceUserService from "../../../services/workspaceUserService";
import CardNotes from "../CardNotes";

type BotOption = {
  id: number;
  name: string;
};

type UserOption = {
  id: number;
  name: string;
};

type ContactSearchField = "name" | "email" | "phone";

type ContactSuggestion = {
  id: number;
  name: string;
  email: string | null;
  phone: string;
};

type UpdateCardProps = {
  mode: "create" | "edit";
  card: GetCardByIdResponse | null;
  pipelineId: number;
  workspaceId: number | null;
  visible: boolean;
  initialStageId?: number | null;
  stages: StageItem[];
  cardLabels: LabelItem[];
  allLabels: LabelItem[];
  allLossReasons: CardLossReasonItem[];
  bots: BotOption[];
  onClose: () => void;
  onSuccess: () => void;
};

const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

const UpdateCard: React.FC<UpdateCardProps> = ({
  mode,
  card,
  pipelineId,
  workspaceId,
  visible,
  initialStageId,
  stages,
  cardLabels,
  allLabels,
  allLossReasons,
  bots,
  onClose,
  onSuccess
}) => {
  const isEdit = mode === "edit";
  const updateCardSchema = v.object({
    contactName: v.string().trim().superRefine(v.nonempty()),
    contactPhone: v
      .string()
      .trim()
      .superRefine(v.nonempty())
      .refine(
        (value) => {
          if (value.startsWith("55")) {
            return /^\d{12,13}$/.test(value);
          }

          return /^\d{10,11}$/.test(value);
        },
        { message: "Formato do telefone inválido" }
      ),
    contactEmail: v.string().trim().superRefine(v.email()).optional(),
    stageId: v
      .number()
      .nullable()
      .refine((value) => value != null, {
        message: "Etapa é obrigatória"
      }),
    labelIds: v.array(v.number()).nullable().optional(),
    lossReasonId: v.number().nullable().optional(),
    userId: v.number().nullable().optional(),
    botId: v.number().nullable().optional(),
    source: v.string().trim().superRefine(v.nonempty()),
    createdByName: v.string().optional(),
    createdAt: v.string().optional(),
    updatedAt: v.string().optional(),
    updatedByName: v.string().optional()
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm<UpdateCardFormValues>({
    defaultValues: {
      contactName: "",
      contactPhone: "55",
      contactEmail: "",
      stageId: null,
      labelIds: [],
      lossReasonId: null,
      userId: null,
      botId: null,
      source: "",
      createdByName: "",
      createdAt: "",
      updatedAt: "",
      updatedByName: ""
    },
    resolver: zodResolver(updateCardSchema),
    shouldFocusError: true
  });

  const toast = useToast();
  const { user, company, hasAnyPermission } = useAuth() as AuthContextType;
  const companyId = user.admin ? company?.id : user.companyId;
  const selectedOptionColor = user.plataform.mainColor || "#2624CD";
  const { createCard, editCard } = usePipelinesApi();
  const { AutocompleteContact } = useContacts();
  const workspaceUserService = WorkspaceUserService();
  const watchedContactName = watch("contactName");
  const watchedContactEmail = watch("contactEmail");
  const watchedContactPhone = watch("contactPhone");
  const inactiveTabColor = "#A9B7C6";
  const [activeContactField, setActiveContactField] =
    useState<ContactSearchField | null>(null);
  const [contactSuggestions, setContactSuggestions] = useState<
    ContactSuggestion[]
  >([]);
  const [selectedStageId, setSelectedStageId] = useState<number | null>(
    card?.stageId ?? null
  );
  const [showUpdateCard, setShowUpdateCard] = useState("data");

  const [selectedLabels, setSelectedLabels] = useState<LabelItem[]>([]);
  const [selectedLossReasonId, setSelectedLossReasonId] = useState<
    number | null
  >(null);
  const [users, setUsers] = useState<UserOption[]>([]);

  const botOptions = bots.map((bot) => ({
    label: bot.name,
    value: bot.id
  }));

  const activeContactValue =
    activeContactField === "name"
      ? watchedContactName
      : activeContactField === "email"
      ? watchedContactEmail ?? ""
      : activeContactField === "phone"
      ? watchedContactPhone ?? ""
      : "";

  const debouncedContactValue = useDebounce(activeContactValue, 600);

  useEffect(() => {
    if (!visible) return;

    setShowUpdateCard("data");
  }, [visible, card?.id, mode]);

  useEffect(() => {
    if (mode === "create") {
      reset({
        contactName: "",
        contactPhone: "55",
        contactEmail: "",
        stageId: initialStageId ?? null,
        labelIds: [],
        lossReasonId: null,
        userId: null,
        botId: null,
        source: "",
        createdByName: "",
        createdAt: "",
        updatedAt: "",
        updatedByName: ""
      });

      setSelectedStageId(initialStageId ?? null);
      setSelectedLabels([]);
      setSelectedLossReasonId(null);
      setContactSuggestions([]);
      setActiveContactField(null);
      return;
    }

    reset({
      contactName: card?.contactName ?? "",
      contactPhone: card?.contactPhone ?? "",
      contactEmail: card?.contactEmail ?? "",
      stageId: card?.stageId ?? null,
      labelIds: cardLabels
        .map((label) => label.id)
        .filter((id): id is number => id != null),
      lossReasonId: card?.lossReasonId ?? null,
      userId: card?.userId ?? null,
      botId: card?.botId ?? null,
      source: card?.source ?? "",
      createdByName: card?.createdByName ?? "",
      createdAt: card?.createdAt ?? "",
      updatedAt: card?.updatedAt ?? "",
      updatedByName: card?.updatedByName ?? ""
    });

    setSelectedStageId(card?.stageId ?? null);
    setSelectedLabels(
      allLabels.filter((label) => card?.labelIds?.includes(label.id))
    );
    setSelectedLossReasonId(card?.lossReasonId ?? null);
    setContactSuggestions([]);
    setActiveContactField(null);
  }, [mode, card, cardLabels, reset, initialStageId]);

  useEffect(() => {
    const searchContacts = async () => {
      if (!companyId || !activeContactField) {
        setContactSuggestions([]);
        return;
      }

      const value = debouncedContactValue.trim();
      const normalizedValue =
        activeContactField === "phone" ? value.replace(/\D/g, "") : value;
      const minLength = activeContactField === "phone" ? 3 : 2;

      if (normalizedValue.length < minLength) {
        setContactSuggestions([]);
        return;
      }

      try {
        const contacts = await AutocompleteContact({
          companyId,
          field: activeContactField,
          value: normalizedValue
        });

        setContactSuggestions(contacts ?? []);
      } catch {
        setContactSuggestions([]);
      }
    };

    searchContacts();
  }, [
    AutocompleteContact,
    activeContactField,
    companyId,
    debouncedContactValue
  ]);

  useEffect(() => {
    const loadUsers = async () => {
      if (!visible) return;

      if (!companyId || !workspaceId) {
        setUsers([]);
        return;
      }

      try {
        const response = await workspaceUserService.getUsersByWorkspace(
          companyId,
          workspaceId
        );
        setUsers(response as UserOption[]);
      } catch (error: any) {
        setUsers([]);

        const backendMessage =
          error?.message ||
          error?.response?.data?.message ||
          "Erro ao listar usuários do workspace.";

        toast?.current?.show({
          life: 5000,
          severity: "error",
          summary: "Erro",
          detail: backendMessage
        });
      }
    };

    loadUsers();
  }, [companyId, visible, workspaceId]);

  const formatDate = (value?: string | null) => {
    if (!value) return "-";
    return new Date(value)
      .toLocaleString("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
      })
      .replace(",", " -");
  };

  const stageOptions = stages.map((stage) => ({
    label: stage.name,
    value: stage.id,
    color: stage.color
  }));

  const labelOptions = allLabels.map((label) => ({
    label: label.name,
    value: label.id,
    color: label.color
  }));

  const lossReasonOptions = allLossReasons.map((lossReason) => ({
    label: lossReason.reason,
    value: lossReason.id
  }));

  const userOptions = users.map((user) => ({
    label: user.name,
    value: user.id
  }));

  const sourceOptions = [
    { label: "Instagram", value: "INSTAGRAM" },
    { label: "Facebook", value: "FACEBOOK" },
    { label: "WhatsApp", value: "WHATSAPP" },
    { label: "CRM", value: "CRM" },
    { label: "LiveChat", value: "LIVECHAT" }
  ];

  const dataTabIsActive = showUpdateCard === "data";
  const historyTabIsActive = showUpdateCard === "note";
  const dataTabColor = dataTabIsActive ? "#fff" : inactiveTabColor;
  const historyTabColor = historyTabIsActive ? "#fff" : inactiveTabColor;

  const handleSelectContactSuggestion = (contact: ContactSuggestion) => {
    setValue("contactName", contact.name ?? "", {
      shouldDirty: true,
      shouldValidate: true
    });
    setValue("contactEmail", contact.email ?? "", {
      shouldDirty: true,
      shouldValidate: true
    });
    setValue("contactPhone", contact.phone ?? "", {
      shouldDirty: true,
      shouldValidate: true
    });
    setContactSuggestions([]);
    setActiveContactField(null);
  };

  const renderContactSuggestions = (field: ContactSearchField) => {
    if (activeContactField !== field || contactSuggestions.length === 0) {
      return null;
    }

    return (
      <ContactSuggestions>
        {contactSuggestions.map((contact) => (
          <ContactSuggestionItem
            key={contact.id}
            type="button"
            onMouseDown={(event) => {
              event.preventDefault();
              handleSelectContactSuggestion(contact);
            }}
          >
            <span className="suggestion-name">
              {contact.name || "Sem nome"}
            </span>
            <span className="suggestion-detail">
              {contact.email || "Sem email"}
            </span>
            <span className="suggestion-detail">
              {contact.phone || "Sem telefone"}
            </span>
          </ContactSuggestionItem>
        ))}
      </ContactSuggestions>
    );
  };

  const handleSubmitCard = async (data: UpdateCardFormValues) => {
    try {
      if (isEdit) {
        const canEditAll =
          user.admin || hasAnyPermission("contactPipeline_edit");

        const isOwnCard = card?.userId != null && card.userId === user.id;

        if (!canEditAll && !isOwnCard) {
          toast?.current?.show({
            severity: "warn",
            summary: "Aviso",
            detail: "Você não tem permissão para editar este card."
          });
          return;
        }

        const payload: EditCardPayload = {
          id: card!.id,
          pipelineId,
          contactName: data.contactName.trim(),
          contactEmail: data.contactEmail?.trim() || "",
          contactPhone: data.contactPhone.trim(),
          stageId: data.stageId ?? null,
          userId: data.userId ?? null,
          botId: data.botId ?? null,
          source: data.source.trim(),
          lossReasonId: selectedLossReasonId,
          labelIds: selectedLabels
            .map((label) => label.id)
            .filter((id): id is number => id != null)
        };

        await editCard(payload);

        toast?.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Card atualizado com sucesso."
        });
      } else {
        const canCreate =
          user.admin || hasAnyPermission("contactPipeline_create");

        if (!canCreate) {
          toast?.current?.show({
            severity: "warn",
            summary: "Aviso",
            detail: "Você não tem permissão para criar card."
          });
          return;
        }

        const payload: SaveCardPayload = {
          pipelineId,
          contactName: data.contactName.trim(),
          contactEmail: data.contactEmail?.trim() || "",
          contactPhone: data.contactPhone.trim(),
          contactWorkspaceId: workspaceId!,
          stageId: data.stageId ?? null,
          userId: data.userId ?? null,
          botId: data.botId ?? null,
          source: data.source.trim(),
          lossReasonId: selectedLossReasonId,
          labelIds: selectedLabels
            .map((label) => label.id)
            .filter((id): id is number => id != null)
        };

        await createCard(payload);

        toast?.current?.show({
          severity: "success",
          summary: "Sucesso",
          detail: "Card criado com sucesso."
        });
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      toast?.current?.show({
        severity: "error",
        summary: "Erro",
        detail:
          error?.response?.data?.message ||
          error?.message ||
          "Erro ao salvar card."
      });
    }
  };

  const isLightColor = (color?: string) => {
    if (!color) return false;

    const hex = color.replace("#", "");

    const normalized =
      hex.length === 3
        ? hex
            .split("")
            .map((char) => char + char)
            .join("")
        : hex;

    if (normalized.length !== 6) return false;

    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);

    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    return brightness > 220;
  };

  return (
    <Container>
      <>
        <style>
          {stages
            .map(
              (stage, index) => `
        .stage-dropdown-panel .p-dropdown-items .p-dropdown-item:nth-child(${
          index + 1
        }) {
          background: ${stage.color} !important;
          color: ${isLightColor(stage.color) ? "#000" : "#fff"} !important;
        }

        .stage-dropdown-panel .p-dropdown-items .p-dropdown-item:nth-child(${
          index + 1
        }).p-highlight {
          background: ${stage.color} !important;
          color: ${isLightColor(stage.color) ? "#000" : "#fff"} !important;
        }

        .stage-dropdown-panel .p-dropdown-items .p-dropdown-item:nth-child(${
          index + 1
        }):not(.p-highlight):not(.p-disabled):hover {
          background: ${stage.color} !important;
          color: ${isLightColor(stage.color) ? "#000" : "#fff"} !important;
        }
      `
            )
            .join("\n")}

          {labelOptions
            .map(
              (label, index) => `
      .label-multiselect-panel .p-multiselect-items .p-multiselect-item:nth-child(${
        index + 1
      }) {
        background: ${label.color} !important;
        color: ${isLightColor(label.color) ? "#000" : "#fff"} !important;
      }

      .label-multiselect-panel .p-multiselect-items .p-multiselect-item:nth-child(${
        index + 1
      }).p-highlight {
        background: ${label.color} !important;
        color: ${isLightColor(label.color) ? "#000" : "#fff"} !important;
      }

      .label-multiselect-panel .p-multiselect-items .p-multiselect-item:nth-child(${
        index + 1
      }):not(.p-highlight):not(.p-disabled):hover {
        background: ${label.color} !important;
        color: ${isLightColor(label.color) ? "#000" : "#fff"} !important;
      }
    `
            )
            .join("\n")}
        </style>

        <OptionsButtons>
          <div
            onClick={() => setShowUpdateCard("data")}
            style={{
              background: dataTabIsActive ? selectedOptionColor : "#fff",
              width: "50%",
              borderRadius: "10px 0 0 10px",
              borderColor: dataTabColor,
              color: dataTabColor
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: "16px",
                height: "16px",
                display: "inline-block",
                backgroundColor: dataTabColor,
                WebkitMaskImage: `url(${Contacts})`,
                WebkitMaskRepeat: "no-repeat",
                WebkitMaskPosition: "center",
                WebkitMaskSize: "contain",
                maskImage: `url(${Contacts})`,
                maskRepeat: "no-repeat",
                maskPosition: "center",
                maskSize: "contain"
              }}
            />
            Dados
          </div>

          {isEdit && (
            <div
              onClick={() => setShowUpdateCard("note")}
              style={{
                background: historyTabIsActive ? selectedOptionColor : "#fff",
                width: "50%",
                borderRadius: "0 10px 10px 0",
                borderColor: historyTabColor,
                color: historyTabColor
              }}
            >
              <i className="pi pi-clock" />
              Histórico
            </div>
          )}
        </OptionsButtons>

        {showUpdateCard === "data" && (
          <>
            <Content>
              <NameIcon>
                <Avatar name={card?.contactName ?? ""} size={60} />
                <div className="divisor">
                  <div className="contactId">
                    {isEdit ? `#${card?.contactId}` : "Novo contato"}
                  </div>
                  <div className="contactName">
                    {isEdit ? card?.contactName : watchedContactName}
                  </div>
                  <div className="contactEmail">
                    {isEdit ? card?.contactEmail : watchedContactEmail}
                  </div>
                </div>
              </NameIcon>

              <div className="hrDiv">
                <hr />
              </div>

              <div className="fields">
                <div className="field">
                  <Controller
                    name="stageId"
                    control={control}
                    render={({ field }) => {
                      const selectedStageOption =
                        stageOptions.find(
                          (option) => option.value === field.value
                        ) ?? null;

                      const textColor = selectedStageOption
                        ? isLightColor(selectedStageOption.color)
                          ? "#000"
                          : "#fff"
                        : "#000";

                      return (
                        <StageDropdown
                          value={field.value}
                          onChange={(e) => {
                            field.onChange(e.value);
                            setSelectedStageId(e.value);
                          }}
                          options={stageOptions}
                          optionLabel="label"
                          optionValue="value"
                          placeholder="Selecione uma etapa"
                          panelClassName="stage-dropdown-panel"
                          appendTo="self"
                          bgColor={selectedStageOption?.color}
                          borderColor={selectedStageOption?.color}
                          textColor={textColor}
                        />
                      );
                    }}
                  />
                  {errors.stageId?.message ? (
                    <div style={{ color: "#AA3939", marginTop: "5px" }}>
                      {errors.stageId.message}
                    </div>
                  ) : null}
                </div>

                <div className="field">
                  <LabelMultiSelectWrapper>
                    <MultiSelect
                      value={selectedLabels
                        .map((label) => label.id)
                        .filter((id): id is number => id != null)}
                      onChange={(e) => {
                        const selectedIds = e.value as number[];

                        const updatedLabels = allLabels.filter(
                          (label) =>
                            label.id != null && selectedIds.includes(label.id)
                        );

                        setSelectedLabels(updatedLabels);
                      }}
                      options={labelOptions}
                      optionLabel="label"
                      optionValue="value"
                      showSelectAll={false}
                      panelClassName="label-multiselect-panel"
                      appendTo="self"
                      placeholder="Etiquetas"
                      display="chip"
                      selectedItemTemplate={(value: number) => {
                        const option = labelOptions.find(
                          (opt) => opt.value === value
                        );
                        if (!option) return null;
                        return (
                          <LabelChip
                            bgColor={option.color}
                            isLight={isLightColor(option.color)}
                          >
                            {option.label}
                          </LabelChip>
                        );
                      }}
                      panelHeaderTemplate={() => null}
                    />
                  </LabelMultiSelectWrapper>
                </div>

                <div className="field">
                  <Controller
                    name="contactName"
                    control={control}
                    render={({ field }) => (
                      <ContactAutocompleteWrapper>
                        <InputText
                          {...field}
                          onChange={(event) => {
                            field.onChange(event);
                            setActiveContactField("name");
                          }}
                          onBlur={() => {
                            field.onBlur();
                            setTimeout(() => {
                              setContactSuggestions([]);
                            }, 150);
                          }}
                          placeholder="Nome do contato"
                          style={{ color: "#6e6b7b", width: "100%" }}
                        />
                        {renderContactSuggestions("name")}
                      </ContactAutocompleteWrapper>
                    )}
                  />
                  {errors.contactName?.message ? (
                    <div style={{ color: "#AA3939", marginTop: "5px" }}>
                      {errors.contactName.message}
                    </div>
                  ) : null}
                </div>

                <div className="field">
                  <Controller
                    name="contactEmail"
                    control={control}
                    render={({ field }) => (
                      <ContactAutocompleteWrapper>
                        <InputText
                          {...field}
                          value={field.value ?? ""}
                          onChange={(event) => {
                            field.onChange(event);
                            setActiveContactField("email");
                          }}
                          onBlur={() => {
                            field.onBlur();
                            setTimeout(() => {
                              setContactSuggestions([]);
                            }, 150);
                          }}
                          placeholder="Email do contato"
                          style={{ color: "#6e6b7b", width: "100%" }}
                        />
                        {renderContactSuggestions("email")}
                      </ContactAutocompleteWrapper>
                    )}
                  />
                  {errors.contactEmail?.message ? (
                    <div style={{ color: "#AA3939", marginTop: "5px" }}>
                      {errors.contactEmail.message}
                    </div>
                  ) : null}
                </div>

                <div className="field">
                  <Controller
                    name="contactPhone"
                    control={control}
                    render={({ field }) => (
                      <ContactAutocompleteWrapper>
                        <PhoneMask
                          value={field.value ?? ""}
                          onChange={(event) => {
                            field.onChange(event);
                            setActiveContactField("phone");
                          }}
                          onBlur={() => {
                            field.onBlur();
                            setTimeout(() => {
                              setContactSuggestions([]);
                            }, 150);
                          }}
                          style={{
                            width: "100%",
                            height: "38px",
                            border: "1px solid #ced4da",
                            borderRadius: "4px",
                            padding: "8px 12px",
                            color: "#6e6b7b",
                            fontSize: "12px"
                          }}
                        />
                        {renderContactSuggestions("phone")}
                      </ContactAutocompleteWrapper>
                    )}
                  />
                  {errors.contactPhone?.message ? (
                    <div style={{ color: "#AA3939", marginTop: "5px" }}>
                      {errors.contactPhone.message}
                    </div>
                  ) : null}
                </div>

                <div className="field">
                  <Controller
                    name="userId"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        value={field.value}
                        onChange={(e) => field.onChange(e.value)}
                        options={userOptions}
                        placeholder="Selecione um usuário"
                        appendTo="self"
                        filter
                        emptyMessage="Nenhum usuário disponível"
                        emptyFilterMessage="Nenhum usuário encontrado"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          height: "38px",
                          color: "#6c757d"
                        }}
                      />
                    )}
                  />
                </div>

                <div className="field">
                  <Controller
                    name="botId"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        value={field.value}
                        onChange={(e) => field.onChange(e.value)}
                        options={botOptions}
                        placeholder="Selecione um bot"
                        appendTo="self"
                        filter
                        emptyMessage="Nenhum bot disponível"
                        emptyFilterMessage="Nenhum bot encontrado"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          height: "38px"
                        }}
                      />
                    )}
                  />
                </div>

                <div className="field">
                  <Controller
                    name="source"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        value={field.value}
                        onChange={(e) => field.onChange(e.value)}
                        options={sourceOptions}
                        placeholder="Selecione uma origem"
                        appendTo="self"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          height: "38px"
                        }}
                      />
                    )}
                  />
                  {errors.source?.message ? (
                    <div style={{ color: "#AA3939", marginTop: "5px" }}>
                      {errors.source.message}
                    </div>
                  ) : null}
                </div>

                <div className="field">
                  <Dropdown
                    value={selectedLossReasonId}
                    options={lossReasonOptions}
                    optionLabel="label"
                    optionValue="value"
                    onChange={(e) =>
                      setSelectedLossReasonId(
                        (e.value as number | null) ?? null
                      )
                    }
                    placeholder="Motivo de não conversão"
                    appendTo="self"
                    showClear
                    emptyMessage="Nenhum motivo de não conversão cadastrado"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      height: "38px"
                    }}
                  />
                </div>

                {isEdit ? (
                  <div className="field">
                    <span className="p-float-label" style={{ width: "100%" }}>
                      <Input
                        id="createdBy"
                        value={`${card?.createdByName ?? "-"} - ${formatDate(
                          card?.createdAt
                        )}`}
                        readOnly={true}
                        style={{ color: "#6c757d" }}
                      />
                      <label
                        style={{
                          marginTop: "0.46rem",
                          backgroundColor: "#FFF",
                          paddingLeft: "0.3rem",
                          paddingRight: "0.3rem"
                        }}
                        htmlFor="createdBy"
                      >
                        Criado por
                      </label>
                    </span>
                  </div>
                ) : null}

                {isEdit ? (
                  <div className="field">
                    <span className="p-float-label" style={{ width: "100%" }}>
                      <Input
                        id="updatedBy"
                        value={`${card?.updatedByName ?? "-"} - ${formatDate(
                          card?.updatedAt
                        )}`}
                        readOnly={true}
                        style={{ color: "#6c757d" }}
                      />
                      <label
                        style={{
                          marginTop: "0.46rem",
                          backgroundColor: "#FFF",
                          paddingLeft: "0.3rem",
                          paddingRight: "0.3rem"
                        }}
                        htmlFor="updatedBy"
                      >
                        Atualizado por
                      </label>
                    </span>
                  </div>
                ) : null}
              </div>
            </Content>

            <div className="buttons">
              <Button
                label="CANCELAR"
                onClick={onClose}
                className="no-hover-button"
              />
              <Button
                onClick={handleSubmit(handleSubmitCard)}
                style={{
                  background: user.plataform.mainColor
                    ? user.plataform.mainColor
                    : "#2624CD",
                  borderColor: user.plataform.mainColor
                    ? user.plataform.mainColor
                    : "#2624CD",
                  color: "#fff"
                }}
              >
                SALVAR
              </Button>
            </div>
          </>
        )}

        {showUpdateCard === "note" && (
          <CardNotes
            cardId={card?.id}
            cardUserId={card?.userId}
            onClose={onClose}
          />
        )}
      </>
    </Container>
  );
};

export default UpdateCard;
